'use client';
import React from 'react';
import { useAtomValue } from 'jotai';

import {
  currentPaginationPageAtom,
  isBlogSearchReadyAtom,
  searchFiltersAtom,
  searchQueryAtom,
} from '@/store/blogStore';
import { useUnifiedBlogData } from '@/hooks/useUnifiedBlogData';
import PaginatedPostsLoader from './PaginatedPostsLoader';
import SinglePostCard from '@/components/blog/SinglePostCard';
import BlogBottomPagination from './BlogBottomPagination';

interface PaginatedPostsProps {
  initialPage?: number;
  initialQuery?: string;
  initialFilters?: { tag: string | null; category: string | null };
}

export default function PaginatedPosts({
  initialPage = 1,
  initialQuery = '',
  initialFilters = { tag: null, category: null },
}: PaginatedPostsProps) {
  const searchQuery = useAtomValue(searchQueryAtom);
  const currentPage = useAtomValue(currentPaginationPageAtom);
  const searchFilters = useAtomValue(searchFiltersAtom);
  const blogSearchIsReady = useAtomValue(isBlogSearchReadyAtom);

  // Use initial values as fallbacks for SSR
  const effectiveQuery = blogSearchIsReady ? searchQuery : initialQuery;
  const effectivePage = blogSearchIsReady ? currentPage : initialPage;
  const effectiveFilters = blogSearchIsReady ? searchFilters : initialFilters;

  const hasSearchFilters = Object.values(effectiveFilters).some((val) => !!val);
  const useAlgolia = !!effectiveQuery || hasSearchFilters;

  const { data, isLoading, isError } = useUnifiedBlogData(
    effectivePage,
    effectiveQuery,
    effectiveFilters,
    useAlgolia
  );

  if (isError) {
    return <div>Error loading posts</div>;
  }

  // only show loader if we're loading AND don't have any data
  if (isLoading && !data) {
    return <PaginatedPostsLoader />;
  }

  const {
    articles = [],
    totalHits = 0,
    totalPages = 0,
    currentPage: pageFrom = 0,
  } = data || {};

  if (!articles || articles?.length === 0) {
    return (
      <div className='py-10 text-center'>
        <h2 className='mb-4 text-2xl font-semibold'>No results found</h2>
        <p className='text-gray-600'>
          Oops! It seems we couldn&apos;t find any articles matching your
          search. Try adjusting your search terms or explore other categories.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className='grid gap-8 sm:grid-cols-2'>
        {articles.map((article: any) => (
          <SinglePostCard key={article._id} post={article} />
        ))}
      </div>
      <BlogBottomPagination
        pageFrom={(pageFrom - 1) * articles.length + 1} // Adjusted to start from 1
        pageTo={
          pageFrom * articles.length > totalHits
            ? totalHits
            : pageFrom * articles.length
        }
        totalHits={totalHits}
        currentPage={effectivePage}
        searchQuery={effectiveQuery}
        searchFilters={effectiveFilters}
        numberOfPages={totalPages}
        tag='results'
      />
    </>
  );
}
